/**
 * Rows to toggle for a shift-click range selection, in display order.
 *
 * Mirror semantics: the range takes the clicked checkbox's resulting state, so
 * `target = !isSelected(clicked)` and only rows whose current state differs from
 * `target` are returned. Returns `null` when the gesture is not a valid range —
 * no anchor, the anchor or clicked row is absent from `orderedRows` (e.g. the
 * anchor is on another page), the anchor is the clicked row, or the anchor is no
 * longer selected — so the caller falls back to a plain single toggle.
 */
export function resolveShiftRange<T>(
  orderedRows: readonly T[],
  anchor: T | null,
  clicked: T,
  isSelected: (row: T) => boolean
): T[] | null {
  if (anchor === null || anchor === clicked) {
    return null;
  }

  const anchorIndex = orderedRows.indexOf(anchor);
  const clickedIndex = orderedRows.indexOf(clicked);
  if (anchorIndex < 0 || clickedIndex < 0) {
    return null;
  }

  // An anchor only marks one end of a range while it is itself selected. Once it has
  // been unticked — on its own, or as part of a range the user deselected — a range
  // grown from it would sweep in every row up to a point nothing marks any more.
  if (!isSelected(anchor)) {
    return null;
  }

  const start = Math.min(anchorIndex, clickedIndex);
  const end = Math.max(anchorIndex, clickedIndex);
  const target = !isSelected(clicked);

  const rowsToToggle: T[] = [];
  for (let i = start; i <= end; i++) {
    const row = orderedRows[i];
    if (isSelected(row) !== target) {
      rowsToToggle.push(row);
    }
  }
  return rowsToToggle;
}
